import { API_ROUTES } from '@/constants/apiRoutes';
import {
  TeammateForSelectItem,
  TeammateListResponse,
  TeammateDetails,
  TeammateFormFields,
} from '@/types/teammates';
import {
  IdempotentRequest,
  UpdateEmailFields,
  UpdatePasswordFields,
  UpdatePhoneFields,
} from '@/types/common';
import { api } from '@/utils/api';

export async function getTeammatesListService(queryParams: string) {
  const { data } = await api<TeammateListResponse>({
    url: `${API_ROUTES.TEAMMATES}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function getTeammatesListForSelectionService(queryParams = '') {
  const { data } = await api<TeammateForSelectItem[]>({
    url: `${API_ROUTES.TEAMMATES}for-selection?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function getTeammatesListForSupportMembersService(
  queryParams = '',
) {
  const { data } = await api<TeammateListResponse>({
    url: `${API_ROUTES.TEAMMATES}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function getTeammateDetailsService(uuid: string) {
  const { data } = await api<TeammateDetails>({
    url: `${API_ROUTES.TEAMMATES}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export const deactivateTeammateService = async (
  uuid: string,
  preflight: boolean,
  teammateId?: string,
) => {
  const payload = teammateId
    ? {
        preflight,
        new_teammate_id: teammateId,
      }
    : { preflight };

  const { data } = await api<{
    mustReplaceTeammate: boolean;
  }>({
    url: `${API_ROUTES.TEAMMATES}${uuid}/deactivate`,
    method: 'post',
    version: 2,
    data: payload,
  });

  return data;
};

export async function reactivateTeammateService(uuid: string) {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${uuid}/reactivate`,
    method: 'post',
    version: 2,
  });

  return data;
}

interface ResetTeammatePasswordProps extends UpdatePasswordFields {
  uuid: string;
}

export const resetTeammatePasswordService = async (
  payload: ResetTeammatePasswordProps,
) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${payload.uuid}/reset-password`,
    method: 'post',
    version: 2,
    data: {
      new_password: payload.password,
      repeat_new_password: payload.confirmPassword,
    },
  });

  return data;
};

export const startTeammateUpdateService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${uuid}/start-auth-update`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const changeTeammateEmailService = async (
  payload: UpdateEmailFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${payload.uuid}/update-email`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_email: payload.email,
      repeat_new_email: payload.confirmEmail,
    },
  });

  return data;
};

export const changeTeammatePhoneService = async (
  payload: UpdatePhoneFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${payload.uuid}/update-phone`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_phone_number: payload.phone,
      repeat_new_phone_number: payload.confirmPhone,
    },
  });

  return data;
};

export const lockTeammateService = async (teammateId: string) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${teammateId}/lock`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const unlockTeammateService = async (teammateId: string) => {
  const { data } = await api({
    url: `${API_ROUTES.TEAMMATES}${teammateId}/unlock`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const addTeammateService = async (
  payload: IdempotentRequest<TeammateFormFields>,
) => {
  const requestData = {
    first_name: payload.firstName,
    last_name: payload.lastName,
    email: payload.email,
    role: payload.role,
    title: payload.title,
    cell_phone_number: payload.cellPhoneNumber,
    business_phone_number: payload.businessPhoneNumber,
    company_id: payload.companyId,
    brand_office_name: payload.branchOfficeName || null,
    bio: payload.bio || null,
    members: payload.members,
    avatar: payload.avatarUrl || null,
  };

  const { data } = await api<string>({
    url: `${API_ROUTES.TEAMMATES}`,
    method: 'post',
    version: 2,
    data: requestData,
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const editTeammateService = async (
  payload: IdempotentRequest<TeammateFormFields>,
) => {
  const requestData = {
    first_name: payload.firstName,
    last_name: payload.lastName,
    email: payload.email,
    role: payload.role,
    title: payload.title,
    cell_phone_number: payload.cellPhoneNumber,
    business_phone_number: payload.businessPhoneNumber,
    company_id: payload.companyId,
    brand_office_name: payload.branchOfficeName || null,
    bio: payload.bio || null,
    members: payload.members,
    avatar: payload.avatarUrl || null,
    alias: payload.alias || null,
  };

  const { data } = await api<string>({
    url: `${API_ROUTES.TEAMMATES}${payload.uuid}`,
    method: 'put',
    version: 2,
    data: requestData,
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};
